/**
 * i18n language configuration.
 *
 * Single source of truth for every supported language. Adding a new language is
 * a two-step change: add an entry here and drop a matching JSON file in
 * `./locales`. Nothing else in the app hard-codes the language list.
 *
 * @format
 */

export type LanguageDirection = "ltr" | "rtl";

export interface LanguageMeta {
	/** BCP-47 / i18next language code. */
	code: string;
	/** English label (kept for accessibility + analytics). */
	label: string;
	/** Native endonym shown in the switcher. */
	nativeName: string;
	/** Flag asset served from /public/flags. */
	flag: string;
	/** Text direction — drives the <html dir> attribute. */
	dir: LanguageDirection;
}

export const LANGUAGES: LanguageMeta[] = [
	{ code: "en", label: "English", nativeName: "English", flag: "/flags/English.png", dir: "ltr" },
	{ code: "de", label: "German", nativeName: "Deutsch", flag: "/flags/german.png", dir: "ltr" },
	{ code: "it", label: "Italian", nativeName: "Italiano", flag: "/flags/italian.png", dir: "ltr" },
	{ code: "fr", label: "French", nativeName: "Français", flag: "/flags/french.png", dir: "ltr" },
	{ code: "ar", label: "Arabic", nativeName: "العربية", flag: "/flags/arabic.png", dir: "rtl" },
	{ code: "es", label: "Spanish", nativeName: "Español", flag: "/flags/spanish.png", dir: "ltr" },
	{ code: "sv", label: "Swedish", nativeName: "Svenska", flag: "/flags/swedish.png", dir: "ltr" },
	{ code: "ja", label: "Japanese", nativeName: "日本語", flag: "/flags/japanese.png", dir: "ltr" },
];

export const DEFAULT_LANGUAGE = "en";

/** localStorage key used to persist the user's selection across reloads. */
export const LANGUAGE_STORAGE_KEY = "positron.lang";

export const SUPPORTED_LANGUAGE_CODES = LANGUAGES.map((l) => l.code);

export function getLanguageMeta(code: string): LanguageMeta {
	const base = code.split("-")[0];
	return (
		LANGUAGES.find((l) => l.code === code || l.code === base) ??
		LANGUAGES[0]
	);
}

export function isRtl(code: string): boolean {
	return getLanguageMeta(code).dir === "rtl";
}
